import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ROUTES } from "@/app/routes";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import type { FeatureKey } from "@/shared/entitlements/entitlements.constants";
import { resolveFeatureLabel } from "@/shared/entitlements/entitlements.constants";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null while the dialog is closing (avoids a flash of "This feature" as
   *  the close animation plays). */
  feature: FeatureKey | null;
}

/// Shared "needs a higher plan" prompt (W4 locked decision: lock + upgrade
/// CTA, not hide). Opened from two independent places, a locked sidebar nav
/// item (`sidebar-nav.tsx`) and a gated route body (`feature-gate.tsx`); each
/// owns its own `open` state and renders its own instance, no shared context
/// needed since the two dialogs never appear at once.
///
/// Fix (final review, IMPORTANT 5): self-serve checkout is built and works
/// (the LTD pricing page + Razorpay checkout, this same billing surfaces
/// plan), so "get in touch about upgrading" was stale copy left over from
/// before it existed, and this CTA now sends the buyer straight to
/// `/pricing`, the highest-intent moment in the whole upgrade path (someone
/// just clicked a locked feature) landing on the one page that can actually
/// sell them the upgrade, not a page that only describes what they already
/// have. This used to point at `/account/billing` (own current plan +
/// purchase history), which is a real destination but not this dialog's
/// job: an account's own billing summary is one click away from
/// `/account/settings` (any authenticated user) or the command menu, so
/// this dialog's one CTA is reserved for the action, not the summary.
export function UpgradeDialog({ open, onOpenChange, feature }: Props): ReactNode {
  const label = feature ? resolveFeatureLabel(feature) : "This feature";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upgrade to unlock {label}</DialogTitle>
          <DialogDescription>
            {label} is not included on your current plan. See our plans to upgrade.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          <Button asChild onClick={() => onOpenChange(false)}>
            <Link to={ROUTES.pricing.path}>See plans</Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
